import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SearchOff from '@mui/icons-material/SearchOff';
import Autocomplete from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useThrottle } from 'hooks/useThrottle';
import { useAllFlexDataLinks } from 'pages/flex-data/flex-data-links/useAllFlexDataLinks';
import type { FlexDataLink } from 'pages/flex-data/flex-data-links/types';

interface CrmObjectAutocompleteProps {
  excludedObjectIds: Set<string>;
  excludedNormalizedTypes: Set<string>;
  onSelect: (selected: FlexDataLink) => void;
  disabled?: boolean;
}

const toGroupLabel = (link: FlexDataLink) => `${link.dataSourceType} ${link.objectTypeLabel}`;

export function CrmObjectAutocomplete({ excludedObjectIds, excludedNormalizedTypes, onSelect, disabled }: CrmObjectAutocompleteProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const throttledInputValue = useThrottle(inputValue, 300);

  const { allFlexDataLinks, isLoading } = useAllFlexDataLinks(throttledInputValue);

  const options = allFlexDataLinks
    .filter((link) => !excludedObjectIds.has(`${link.dataSourceType}|${link.tenantId}|${link.objectId}`))
    .filter((link) => !excludedNormalizedTypes.has(link.normalizedType))
    .sort((a, b) => {
      const groupCompare = toGroupLabel(a).localeCompare(toGroupLabel(b));
      return groupCompare !== 0 ? groupCompare : a.objectLabel.localeCompare(b.objectLabel);
    });

  const handleChange = (_: React.SyntheticEvent, selected: FlexDataLink | null) => {
    if (selected) {
      onSelect(selected);
      setInputValue('');
      setIsDropdownOpen(false);
    }
  };

  return (
    <Stack direction="column">
      <Autocomplete<FlexDataLink>
        fullWidth
        forcePopupIcon={false}
        disabled={disabled}
        options={options}
        filterOptions={(x) => x}
        loading={isLoading}
        value={null}
        inputValue={inputValue}
        getOptionLabel={(option) => option.objectLabel}
        groupBy={toGroupLabel}
        open={isDropdownOpen && throttledInputValue.length >= 3}
        onOpen={() => setIsDropdownOpen(true)}
        onClose={() => setIsDropdownOpen(false)}
        noOptionsText={
          throttledInputValue.length >= 3 && !isLoading && options.length === 0 ? (
            <Stack direction="row" alignItems="center" gap={1.5}>
              <SearchOff fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body1">
                {t('editor.side_panel.link_crm_object_modal.no_results')}
                <br />
                <Trans i18nKey="editor.side_panel.link_crm_object_modal.no_results_troubleshooting">
                  {"If you're having issues, see the "}
                  <a
                    href="https://support.proposify.com/hc/en-us/articles/37747462615195-Integration-connectivity-problems"
                    target="_blank"
                    rel="noreferrer"
                  >
                    troubleshooting page
                  </a>
                  {'.'}
                </Trans>
              </Typography>
            </Stack>
          ) : null
        }
        onInputChange={(_, newValue) => setInputValue(newValue)}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('editor.side_panel.link_crm_object_modal.search_label')}
            placeholder={t('editor.side_panel.link_crm_object_modal.search_placeholder')}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={params.InputProps}
          />
        )}
      />
      <FormHelperText sx={{ padding: '0 14px' }}>{t('editor.side_panel.link_crm_object_modal.helper_text')}</FormHelperText>
    </Stack>
  );
}
